import { useEffect, useId, useRef, useState } from "react";
import { Building2, Loader2, MapPin, Plane, TrainFront } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const SuggestionIcon = ({ kind, dark }) => {
  const value = String(kind || "").toLowerCase();
  const Icon = value.includes("airport") || value.includes("aerodrome") ? Plane
    : value.includes("station") || value.includes("rail") ? TrainFront
    : value.includes("hotel") ? Building2
    : MapPin;
  return <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${dark ? "text-miles-electric" : "text-miles"}`} strokeWidth={1.5} />;
};


export default function AddressAutocomplete({
  label,
  icon: Icon,
  value,
  onChange,
  onSelect,
  placeholder,
  testId,
  dark = false,
  inputClass,
  selectValue,
  bias,
}) {
  const { lang, t } = useLang();
  const listId = useId();
  const rootRef = useRef(null);
  const abortRef = useRef(null);
  const skipNextSearchRef = useRef(false);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    const query = value.trim();
    abortRef.current?.abort();
    setError("");

    if (!hasInteracted) {
      setItems([]);
      setOpen(false);
      setLoading(false);
      return undefined;
    }

    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      setItems([]);
      setOpen(false);
      setLoading(false);
      return undefined;
    }

    if (query.length < 2) {
      setItems([]);
      setOpen(false);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const biasQuery = bias && typeof bias.lat === "number" && typeof bias.lon === "number"
          ? `&lat=${bias.lat}&lon=${bias.lon}` : "";
        const response = await fetch(
          `${API}/geocoding/autocomplete?q=${encodeURIComponent(query)}&lang=${encodeURIComponent(lang)}${biasQuery}`,
          { signal: controller.signal, headers: { Accept: "application/json" } },
        );
        if (!response.ok) throw new Error("autocomplete");
        const data = await response.json();
        if (controller.signal.aborted) return;
        setItems(data.features || []);
        setActive(-1);
        setOpen(true);
      } catch (err) {
        if (controller.signal.aborted) return;
        setItems([]);
        setError(t("addr.unavailable"));
        setOpen(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 280);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [value, lang, t, hasInteracted]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  const choose = (item) => {
    skipNextSearchRef.current = true;
    onChange(selectValue ? selectValue(item) : item.label);
    onSelect?.(item);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (event) => {
    if (!open || !items.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => (index + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => (index - 1 + items.length) % items.length);
    } else if (event.key === "Enter" && active >= 0) {
      event.preventDefault();
      choose(items[active]);
    } else if (event.key === "Escape") {
      setOpen(false);
      setActive(-1);
    }
  };

  const inputClasses = dark
    ? "w-full bg-transparent pr-7 text-sm font-medium text-white outline-none placeholder:text-white/35"
    : (inputClass || "w-full rounded-2xl border border-ink/10 bg-white px-4 py-3.5 pr-10 text-sm font-medium text-ink outline-none transition-colors placeholder:font-normal placeholder:text-slate/60 focus:border-miles focus:ring-4 focus:ring-miles/10");

  return (
    <div ref={rootRef} className="relative w-full">
      {label && (
        <label htmlFor={testId} className={`mb-2 flex items-center gap-2 font-semibold uppercase ${dark ? "text-[10px] tracking-[0.2em] text-white/55" : "text-xs tracking-[0.15em] text-slate"}`}>
          {Icon && <Icon className={`${dark ? "h-3 w-3" : "h-3.5 w-3.5"} text-miles`} strokeWidth={1.5} />}
          {label}
        </label>
      )}
      <input
        id={testId}
        value={value}
        onChange={(event) => {
          setHasInteracted(true);
          onChange(event.target.value);
          onSelect?.(null);
          setOpen(false);
        }}
        onFocus={() => {
          setHasInteracted(true);
          if (items.length) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        data-testid={testId}
        className={inputClasses}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-controls={`${listId}-listbox`}
        aria-expanded={open}
        aria-activedescendant={active >= 0 ? `${listId}-option-${active}` : undefined}
      />
      {loading && (
        <Loader2
          className={`pointer-events-none absolute ${dark ? "right-0 top-1/2 -translate-y-1/2" : "right-4 top-1/2 -translate-y-1/2"} h-4 w-4 animate-spin ${dark ? "text-miles-electric" : "text-miles"}`}
          strokeWidth={1.5}
          aria-label={t("addr.loading")}
        />
      )}

      {open && value.trim().length >= 2 && (
        <div
          id={`${listId}-listbox`}
          role="listbox"
          aria-label={label}
          data-testid={`${testId}-results`}
          className={`absolute left-0 right-0 z-[80] mt-2 overflow-hidden rounded-2xl border shadow-[0_24px_70px_rgba(7,12,26,0.25)] ${
            dark ? "border-white/10 bg-miles-deep/95 text-white backdrop-blur-xl" : "border-ink/10 bg-white text-ink"
          }`}
        >
          {items.map((item, index) => (
            <button
              key={`${item.id}-${index}`}
              id={`${listId}-option-${index}`}
              type="button"
              role="option"
              aria-selected={index === active}
              data-testid={`${testId}-option-${index}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(item)}
              onMouseEnter={() => setActive(index)}
              className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${
                index === active
                  ? dark ? "bg-white/10" : "bg-miles-ice"
                  : dark ? "hover:bg-white/5" : "hover:bg-cloud"
              }`}
            >
              <SuggestionIcon kind={item.kind} dark={dark} />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold">{item.name || item.label}</span>
                <span className={`mt-0.5 block truncate text-xs ${dark ? "text-white/55" : "text-slate"}`}>{item.label}</span>
              </span>
            </button>
          ))}
          {!loading && !items.length && (
            <p className={`px-4 py-3 text-xs leading-relaxed ${dark ? "text-white/60" : "text-slate"}`} data-testid={`${testId}-empty`}>
              {error || t("addr.empty")}
            </p>
          )}
          <p className={`border-t px-4 py-2 font-mono text-[10px] ${dark ? "border-white/10 text-white/35" : "border-ink/5 text-slate/70"}`}>
            {t("addr.attribution")}
          </p>
        </div>
      )}
    </div>
  );
}
